import React from "react";
import {
  View,
  Pressable,
  Image,
  ViewStyle,
  ImageStyle,
  DimensionValue,
  AnimatableNumericValue,
} from "react-native";
import { BoxProps } from "@xsolla/xui-primitives-core";

export const Box: React.FC<BoxProps> = ({
  children,
  onPress,
  onLayout,
  onMoveShouldSetResponder,
  onResponderGrant,
  onResponderMove,
  onResponderRelease,
  onResponderTerminate,
  backgroundColor,
  borderColor,
  borderWidth,
  borderBottomWidth,
  borderBottomColor,
  borderTopWidth,
  borderTopColor,
  borderLeftWidth,
  borderLeftColor,
  borderRightWidth,
  borderRightColor,
  borderRadius,
  borderStyle,
  height,
  padding,
  paddingHorizontal,
  paddingVertical,
  margin,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  flexDirection,
  alignItems,
  justifyContent,
  position,
  top,
  bottom,
  left,
  right,
  width,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  flex,
  overflow,
  zIndex,
  hoverStyle,
  pressStyle,
  style,
  "data-testid": dataTestId,
  testID,
  as,
  src,
  alt,
  ...rest
}) => {
  const getContainerStyle = (pressed?: boolean): ViewStyle => ({
    backgroundColor:
      pressed && pressStyle?.backgroundColor
        ? pressStyle.backgroundColor
        : backgroundColor,
    borderColor,
    borderWidth,
    borderBottomWidth,
    borderBottomColor,
    borderTopWidth,
    borderTopColor,
    borderLeftWidth,
    borderLeftColor,
    borderRightWidth,
    borderRightColor,
    borderRadius: borderRadius as AnimatableNumericValue,
    borderStyle: borderStyle as ViewStyle["borderStyle"],
    overflow,
    zIndex,
    height: height as DimensionValue,
    width: width as DimensionValue,
    minWidth: minWidth as DimensionValue,
    minHeight: minHeight as DimensionValue,
    maxWidth: maxWidth as DimensionValue,
    maxHeight: maxHeight as DimensionValue,
    padding: padding as DimensionValue,
    paddingHorizontal: paddingHorizontal as DimensionValue,
    paddingVertical: paddingVertical as DimensionValue,
    margin: margin as DimensionValue,
    marginTop: marginTop as DimensionValue,
    marginBottom: marginBottom as DimensionValue,
    marginLeft: marginLeft as DimensionValue,
    marginRight: marginRight as DimensionValue,
    flexDirection,
    alignItems,
    justifyContent,
    position: position as ViewStyle["position"],
    top: top as DimensionValue,
    bottom: bottom as DimensionValue,
    left: left as DimensionValue,
    right: right as DimensionValue,
    flex,
    ...(style as ViewStyle),
  });

  const finalTestID = dataTestId || testID;

  // Destructure and drop web-only props from rest before passing to RN components
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    role,
    tabIndex,
    onKeyDown,
    onKeyUp,
    "aria-label": _ariaLabel,
    "aria-labelledby": _ariaLabelledBy,
    "aria-current": _ariaCurrent,
    "aria-disabled": _ariaDisabled,
    "aria-live": _ariaLive,
    className,
    "data-testid": _dataTestId,
    ...nativeRest
  } = rest as Record<string, unknown>;

  // Handle as="img" for React Native
  if (as === "img" && src) {
    const imageStyle: ImageStyle = {
      width: width as DimensionValue,
      height: height as DimensionValue,
      borderRadius: borderRadius as number,
      position: position as ImageStyle["position"],
      top: top as DimensionValue,
      bottom: bottom as DimensionValue,
      left: left as DimensionValue,
      right: right as DimensionValue,
      ...(style as ImageStyle),
    };

    return (
      <Image
        source={{ uri: src }}
        style={imageStyle}
        testID={finalTestID}
        resizeMode="cover"
        {...nativeRest}
      />
    );
  }

  if (onPress) {
    return (
      <Pressable
        onPress={onPress}
        onLayout={onLayout}
        onMoveShouldSetResponder={onMoveShouldSetResponder}
        onResponderGrant={onResponderGrant}
        onResponderMove={onResponderMove}
        onResponderRelease={onResponderRelease}
        onResponderTerminate={onResponderTerminate}
        style={({ pressed }) => getContainerStyle(pressed)}
        testID={finalTestID}
        {...nativeRest}
      >
        {children}
      </Pressable>
    );
  }

  return (
    <View
      style={getContainerStyle()}
      testID={finalTestID}
      onLayout={onLayout}
      onMoveShouldSetResponder={onMoveShouldSetResponder}
      onResponderGrant={onResponderGrant}
      onResponderMove={onResponderMove}
      onResponderRelease={onResponderRelease}
      onResponderTerminate={onResponderTerminate}
      {...nativeRest}
    >
      {children}
    </View>
  );
};
